import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../redux/thunk/authThunk';
import MixGoatLogo from '../MixGoatLogo';

/**
 * DeleteAccountModal
 *
 * Native-only (iOS/Android) in-app account deletion, required by App Store
 * Review Guideline 5.1.1(v). Flow:
 *   - explains the deletion is permanent and lists what is removed
 *   - reminds the user that an App Store / Play subscription must be cancelled
 *     separately (we cannot cancel store IAPs on their behalf)
 *   - requires typing DELETE to confirm (works for every account type)
 *   - calls window.electron.auth.deleteAccount, then shows a goodbye screen
 *     and tears down the session when the user taps Done
 *
 * Colors use theme variables only (per project convention).
 */
function DeleteAccountModal({ onClose }) {
  const dispatch = useDispatch();
  const accessToken = useSelector((s) => s.auth.accessToken);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleted, setDeleted] = useState(false);

  const storeName = (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'android')
    ? 'Google Play' : 'the App Store';

  const canSubmit = confirmText.trim().toUpperCase() === 'DELETE' && !submitting;

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await window.electron.auth.deleteAccount(accessToken, { confirm: true });
      if (res && res.error) {
        setError(res.error);
        setSubmitting(false);
        return;
      }
      // Success — show the goodbye screen; the session is torn down only when
      // the user taps "Done" (or dismisses), so they actually read the message.
      setDeleted(true);
      setSubmitting(false);
    } catch (err) {
      setError((err && err.message) || 'Failed to delete account');
      setSubmitting(false);
    }
  }, [accessToken]);

  // Ends the now-orphaned session and returns to the login screen.
  const finish = useCallback(() => {
    dispatch(logoutUser());
  }, [dispatch]);

  const closeOnBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (deleted) { finish(); return; }
    if (!submitting) onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={closeOnBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 420,
          background: 'var(--color-bg-primary)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border)',
          borderRadius: 16,
          padding: 'calc(env(safe-area-inset-top, 0) + 20px) 20px calc(env(safe-area-inset-bottom, 0) + 20px)',
          display: 'flex', flexDirection: 'column', gap: 14,
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {deleted ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center' }} aria-hidden>
              <MixGoatLogo width={64} height={64} />
            </div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, textAlign: 'center' }}>
              Your account has been deleted
            </h2>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--color-text-secondary)', textAlign: 'center' }}>
              Your MixGoat account and data are gone for good. Thanks for spending
              some time with us — no hard feelings. 💙
            </p>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--color-text-secondary)', textAlign: 'center' }}>
              You're always welcome back — just sign up again anytime and we'll
              roll out the GOAT carpet. 🐐
            </p>
            <button
              type="button"
              onClick={finish}
              style={{
                minHeight: 46, borderRadius: 10, border: 'none', marginTop: 4,
                background: 'var(--color-accent)', color: '#fff',
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--color-danger)' }}>
              Delete your account
            </h2>

            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
              This permanently deletes your MixGoat account and all associated data —
              your liked tracks, followed artists, playlists, notifications and
              preferences. <strong style={{ color: 'var(--color-text-primary)' }}>This cannot be undone.</strong>
            </p>

            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--color-text-tertiary)' }}>
              If you subscribed through {storeName}, your subscription must be
              cancelled separately in your {storeName} account settings — deleting
              your account here does not cancel store billing.
            </p>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Type DELETE to confirm
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoCapitalize="characters"
                autoCorrect="off"
                placeholder="DELETE"
                style={{
                  padding: '10px 12px', borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg-input)',
                  color: 'var(--color-text-primary)', fontSize: 14,
                }}
              />
            </label>

            {error && (
              <div style={{ color: 'var(--color-danger)', fontSize: 13 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{
                  flex: 1, minHeight: 44, borderRadius: 10,
                  border: '1px solid var(--color-border)',
                  background: 'transparent', color: 'var(--color-text-primary)',
                  fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!canSubmit}
                style={{
                  flex: 1, minHeight: 44, borderRadius: 10, border: 'none',
                  background: 'var(--color-danger)', color: '#fff',
                  fontSize: 15, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed',
                  opacity: canSubmit ? 1 : 0.5,
                }}
              >
                {submitting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DeleteAccountModal;
